/** Cross section of a hot-rolled I/H profile in metres (EN 10365). */
export interface SteelProfile {
  /** Height (along the web). */
  readonly h: number;
  /** Flange width. */
  readonly b: number;
  /** Flange thickness. */
  readonly tf: number;
  /** Web thickness. */
  readonly tw: number;
}

const mm = (h: number, b: number, tf: number, tw: number): SteelProfile => ({
  h: h / 1000,
  b: b / 1000,
  tf: tf / 1000,
  tw: tw / 1000,
});

export const STEEL_PROFILES = {
  'HEA 160': mm(152, 160, 9, 6),
  'HEA 180': mm(171, 180, 9.5, 6),
  'HEA 200': mm(190, 200, 10, 6.5),
  'HEA 220': mm(210, 220, 11, 7),
  'HEA 240': mm(230, 240, 12, 7.5),
  'HEA 260': mm(250, 260, 12.5, 7.5),
  'HEA 280': mm(270, 280, 13, 8),
  'HEA 300': mm(290, 300, 14, 8.5),
  'HEA 320': mm(310, 300, 15.5, 9),
  'HEA 340': mm(330, 300, 16.5, 9.5),
  'HEA 360': mm(350, 300, 17.5, 10),
  'HEA 400': mm(390, 300, 19, 11),
  'HEA 450': mm(440, 300, 21, 11.5),
  'HEA 500': mm(490, 300, 23, 12),
  'HEA 550': mm(540, 300, 24, 12.5),
  'HEA 600': mm(590, 300, 25, 13),
  'HEA 650': mm(640, 300, 26, 13.5),
  'HEB 160': mm(160, 160, 13, 8),
  'HEB 180': mm(180, 180, 14, 8.5),
  'HEB 200': mm(200, 200, 15, 9),
  'HEB 220': mm(220, 220, 16, 9.5),
  'HEB 240': mm(240, 240, 17, 10),
  'HEB 260': mm(260, 260, 17.5, 10),
  'HEB 280': mm(280, 280, 18, 10.5),
  'HEB 300': mm(300, 300, 19, 11),
  'HEB 340': mm(340, 300, 21.5, 12),
  'HEB 400': mm(400, 300, 24, 13.5),
  'HEB 450': mm(450, 300, 26, 14),
  'HEB 500': mm(500, 300, 28, 14.5),
  'IPE 200': mm(200, 100, 8.5, 5.6),
  'IPE 220': mm(220, 110, 9.2, 5.9),
  'IPE 240': mm(240, 120, 9.8, 6.2),
  'IPE 270': mm(270, 135, 10.2, 6.6),
  'IPE 300': mm(300, 150, 10.7, 7.1),
  'IPE 330': mm(330, 160, 11.5, 7.5),
  'IPE 360': mm(360, 170, 12.7, 8),
  'IPE 400': mm(400, 180, 13.5, 8.6),
  'IPE 450': mm(450, 190, 14.6, 9.4),
  'IPE 500': mm(500, 200, 16, 10.2),
  'IPE 550': mm(550, 210, 17.2, 11.1),
  'IPE 600': mm(600, 220, 19, 12),
} as const satisfies Record<string, SteelProfile>;

export type SteelProfileId = keyof typeof STEEL_PROFILES;

export const STEEL_PROFILE_IDS = Object.keys(STEEL_PROFILES) as [
  SteelProfileId,
  ...SteelProfileId[],
];

export type SectionShape = 'I' | 'C' | 'tube' | 'rod';

/** Any member cross section; `t` is the wall thickness of C profiles and tubes. */
export interface Section {
  readonly shape: SectionShape;
  readonly h: number;
  readonly b: number;
  readonly tf: number;
  readonly tw: number;
}

const light = (shape: SectionShape, h: number, b: number, t: number): Section => ({
  shape,
  h: h / 1000,
  b: b / 1000,
  tf: t / 1000,
  tw: t / 1000,
});

/** Cold-formed purlins and girts, square tubes for struts, round bars for bracing. */
export const LIGHT_SECTIONS = {
  'C 160': light('C', 160, 65, 2.5),
  'C 200': light('C', 200, 70, 2.5),
  'C 250': light('C', 250, 75, 3),
  'C 300': light('C', 300, 85, 3),
  'RR 100': light('tube', 100, 100, 5),
  'RR 120': light('tube', 120, 120, 5),
  'RR 140': light('tube', 140, 140, 6),
  'Rd 20': light('rod', 20, 20, 20),
  'Rd 24': light('rod', 24, 24, 24),
} as const satisfies Record<string, Section>;

export type SectionId = SteelProfileId | keyof typeof LIGHT_SECTIONS;

export const STEEL_DENSITY = 7850;

/**
 * Weight per metre (kg/m) from the cross section; rolled I profiles get 5 % for the root
 * radii the simple outline leaves out.
 */
export function sectionMass(id: SectionId): number {
  const { shape, h, b, tf, tw } = sectionOf(id);
  const area =
    shape === 'I'
      ? (2 * b * tf + (h - 2 * tf) * tw) * 1.05
      : shape === 'C'
        ? (h + 2 * b) * tw
        : shape === 'tube'
          ? 4 * (b - tw) * tw
          : (Math.PI * b * b) / 4;
  return area * STEEL_DENSITY;
}

export function sectionOf(id: SectionId): Section {
  if (id in LIGHT_SECTIONS) return LIGHT_SECTIONS[id as keyof typeof LIGHT_SECTIONS];
  return { shape: 'I', ...STEEL_PROFILES[id as SteelProfileId] };
}
